import React from 'react'

import * as TYPES from './types'

import Landing from '../../Containers/Landing'

import { withReduxAuth } from '../../Redux/indexAuth'
import { showCover, hideCover } from '../../utils/cover'

// Loaded only on authorized pages (AdminLTE bundles Bootstrap 4; Bootstrap 5 must not coexist)
const AUTH_CDN_LINKS = [
  { id: 'adminlte-css',     href: 'https://cdn.jsdelivr.net/npm/admin-lte@3.2/dist/css/adminlte.min.css' },
  { id: 'ionicons-css',    href: 'https://code.ionicframework.com/ionicons/2.0.1/css/ionicons.min.css' },
  { id: 'fa-all-css',      href: 'https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.7.2/css/all.min.css' },
  { id: 'fa-brands-css',   href: 'https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.7.2/css/brands.min.css' },
  { id: 'primeicons-css',  href: 'https://cdn.jsdelivr.net/npm/primeicons@7.0.0/primeicons.css' },
  { id: 'primereact-theme', href: 'https://cdn.jsdelivr.net/npm/primereact@latest/resources/themes/bootstrap4-dark-blue/theme.css' },
];

// Loaded only on public pages (react-bootstrap Modal + utility classes)
const PUBLIC_CDN_LINKS = [
  { id: 'bootstrap5-css', href: 'https://cdn.jsdelivr.net/npm/bootstrap@5.3.3/dist/css/bootstrap.min.css' },
];

const loadLinks = (links: { id: string; href: string }[]) => {
  links.forEach(({ id, href }) => {
    if (!document.getElementById(id)) {
      const link = document.createElement('link');
      link.id = id;
      link.rel = 'stylesheet';
      link.href = href;
      document.head.appendChild(link);
    }
  });
};

const unloadLinks = (links: { id: string }[]) => {
  links.forEach(({ id }) => {
    const el = document.getElementById(id);
    if (el) document.head.removeChild(el);
  });
};

/**
 * loadAuthorizedTheme(onReady?)
 *
 * Swaps the CDN stylesheet set from public → authorized and notifies the caller
 * once the browser has fully parsed AND painted the new stylesheets.
 *
 * Steps:
 *  1. Remove Bootstrap 5 <link> tags (PUBLIC_CDN_LINKS) first — prevents it
 *     from conflicting with AdminLTE's bundled Bootstrap 4.
 *  2. Collect only AUTH_CDN_LINKS entries whose <link id="…"> does NOT yet
 *     exist in <head> — avoids duplicate injections on repeated logins.
 *  3. If nothing is pending (all links already cached from a previous login),
 *     onReady() fires synchronously and returns immediately.
 *  4. For each missing link, a <link rel="stylesheet"> is created with a shared
 *     onload/onerror counter and appended to <head>.
 *  5. Once every pending link resolves (success or error):
 *     - `document.documentElement.offsetHeight` is read synchronously, forcing
 *       Chrome to flush pending style recalculations and apply all CSS rules to
 *       the DOM before this line returns (critical after a hard reload when
 *       stylesheets are not browser-cached).
 *     - rAF 1: browser performs layout with the newly applied styles.
 *     - rAF 2: browser composites and paints the styled frame to screen.
 *     Only then is onReady() invoked — so hideCover() fades out over a
 *     fully-styled UI with no flash of unstyled content (FOUC).
 *
 * Stylesheets injected (AUTH_CDN_LINKS):
 *  - adminlte-css      : AdminLTE 3.2 layout + components (bundles Bootstrap 4)
 *  - ionicons-css      : Ionicons 2.0.1 icon font
 *  - fa-all-css        : Font Awesome 6.7.2 core styles
 *  - fa-brands-css     : Font Awesome 6.7.2 brand icons
 *  - primeicons-css    : PrimeIcons 7.0.0 (used by PrimeReact components)
 *  - primereact-theme  : PrimeReact Bootstrap4-dark-blue theme tokens
 *
 * Cover helpers (showCover / hideCover) are imported from utils/cover.ts and
 * called by componentDidMount / componentDidUpdate — not inside this function.
 */
const loadAuthorizedTheme = (onReady?: () => void) => {
  unloadLinks(PUBLIC_CDN_LINKS);

  const pending = AUTH_CDN_LINKS.filter(({ id }) => !document.getElementById(id));
  if (pending.length === 0) { onReady?.(); return; }

  let loaded = 0;
  pending.forEach(({ id, href }) => {
    const link = document.createElement('link');
    link.id = id; link.rel = 'stylesheet'; link.href = href;
    link.onload = link.onerror = () => {
      if (++loaded === pending.length) {
        // Force a synchronous style recalculation + layout so Chrome applies
        // all newly parsed CSS rules to the DOM before we start fading out.
        // Reading offsetHeight is a well-known "layout flush" trigger.
        void document.documentElement.offsetHeight;
        // Two rAF cycles: rAF1 = styles applied to layout, rAF2 = frame painted.
        requestAnimationFrame(() => requestAnimationFrame(() => onReady?.()));
      }
    };
    document.head.appendChild(link);
  });
};

/**
 * unloadAuthorizedTheme()
 *
 * Swaps the CDN stylesheet set from authorized → public (logout / unauthenticated render).
 *
 * Steps:
 *  1. Remove all AUTH_CDN_LINKS <link> elements from <head>
 *     (AdminLTE, Ionicons, Font Awesome, PrimeIcons, PrimeReact theme).
 *  2. Inject PUBLIC_CDN_LINKS — Bootstrap 5.3.3 — required by react-bootstrap
 *     Modal and utility classes on public pages (Landing, EarlyAccess, etc.).
 *
 * No onReady / rAF delay: public pages render acceptably before Bootstrap 5
 * resolves, and no cover is shown during logout transitions — so there is
 * no risk of FOUC that needs to be hidden behind an overlay.
 */
const unloadAuthorizedTheme = () => {
  unloadLinks(AUTH_CDN_LINKS);
  loadLinks(PUBLIC_CDN_LINKS);
};

/**
 * Authorized Route Class Component
 * Used to redirect not authenticated users to Landing Page
 */
class AuthorizedComponent extends React.Component<TYPES.AuthorizedComponentProps> {
  componentDidMount() {
    const { auth } = this.props;
    if (auth.authToken && this.props.element) {
      showCover();
      loadAuthorizedTheme(() => hideCover());
    } else {
      unloadAuthorizedTheme();
    }
  }

  componentDidUpdate(prevProps: TYPES.AuthorizedComponentProps) {
    const { auth } = this.props;
    if (prevProps.auth.authToken !== auth.authToken) {
      if (auth.authToken && this.props.element) {
        showCover();
        loadAuthorizedTheme(() => hideCover());
      } else {
        unloadAuthorizedTheme();
      }
    }
  }
  public render() {
    const { auth } = this.props;
    if (auth.authToken && this.props.element) return this.props.element;
    else return <Landing/>;
  }
}

export default withReduxAuth(AuthorizedComponent)